import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Brand } from '@/components/Brand';
import { useAuth } from '@/context/AuthContext';
import { roleHome } from '@/lib/format';

const LINKS = [
  { to: '/', label: 'Início' },
  { to: '/galeria', label: 'Viagens' },
  { to: '/quem-somos', label: 'Quem Somos' },
  { to: '/contato', label: 'Contato' },
];

export function PublicNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isHome = location.pathname === '/';
  const solid = scrolled || !isHome;

  return (
    <header className={`sticky top-0 z-50 transition-colors duration-300 ${solid ? 'backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border/60' : 'bg-transparent'}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="public-logo">
          <Brand />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`px-3 py-2 text-sm rounded-md transition-colors hover:bg-accent/60 ${location.pathname === l.to ? 'text-primary font-medium' : 'text-foreground/80'}`}
              data-testid={`public-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <Button className="ml-2" onClick={() => navigate(roleHome(user.role))} data-testid="public-nav-panel">
              Meu Painel
            </Button>
          ) : (
            <Button className="ml-2" onClick={() => navigate('/login')} data-testid="public-nav-login">
              Entrar
            </Button>
          )}
        </nav>

        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="public-nav-mobile-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex items-center gap-2 mb-6">
                <Brand />
              </div>
              <div className="flex flex-col gap-1">
                {LINKS.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                    className="px-3 py-2.5 rounded-md hover:bg-accent/60 text-foreground/90">
                    {l.label}
                  </Link>
                ))}
                <Button className="mt-3" onClick={() => { setOpen(false); navigate(user ? roleHome(user.role) : '/login'); }}>
                  {user ? 'Meu Painel' : 'Entrar'}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
